/**
 * About-page HiBro mark, tapped like Android's build number.
 * Five taps inside {@link DEVELOPER_TAP_GAP_MS} of each other turn on developer
 * options. A longer pause starts the count over. The unlocked flag is a
 * renderer-local preference (`localStorage`), not a configuration key — it
 * only reveals the existing platform settings page.
 */
export const DEVELOPER_OPTIONS_STORAGE_KEY = 'hibro.settings.developerOptions';
export const DEVELOPER_TAP_COUNT = 5;
export const DEVELOPER_TAP_GAP_MS = 2000;

export interface DeveloperOptionsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface DeveloperTapState {
  readonly enabled: boolean;
  readonly remaining: number;
  readonly lastTapAt: number;
}

export type DeveloperTapResult =
  | { readonly kind: 'already' }
  | {
      readonly kind: 'progress';
      readonly remaining: number;
      /** Toast only once the burst is close, same as Android's build number. */
      readonly announce: boolean;
    }
  | { readonly kind: 'unlocked' };

export function initialDeveloperTapState(enabled: boolean): DeveloperTapState {
  return {
    enabled,
    remaining: DEVELOPER_TAP_COUNT,
    lastTapAt: 0,
  };
}

export function readDeveloperEnabled(
  storage: DeveloperOptionsStorage,
): boolean {
  return storage.getItem(DEVELOPER_OPTIONS_STORAGE_KEY) === '1';
}

export function writeDeveloperEnabled(storage: DeveloperOptionsStorage): void {
  storage.setItem(DEVELOPER_OPTIONS_STORAGE_KEY, '1');
}

export function clearDeveloperEnabled(storage: DeveloperOptionsStorage): void {
  storage.removeItem(DEVELOPER_OPTIONS_STORAGE_KEY);
}

export function applyDeveloperTap(
  state: DeveloperTapState,
  now: number,
): { readonly state: DeveloperTapState; readonly result: DeveloperTapResult } {
  if (state.enabled) {
    return { state, result: { kind: 'already' } };
  }
  const burst =
    state.lastTapAt !== 0 && now - state.lastTapAt <= DEVELOPER_TAP_GAP_MS;
  const remaining = (burst ? state.remaining : DEVELOPER_TAP_COUNT) - 1;
  if (remaining <= 0) {
    return {
      state: { enabled: true, remaining: 0, lastTapAt: now },
      result: { kind: 'unlocked' },
    };
  }
  return {
    state: { enabled: false, remaining, lastTapAt: now },
    result: {
      kind: 'progress',
      remaining,
      announce: remaining <= DEVELOPER_TAP_COUNT - 3,
    },
  };
}
